<template>
  <div>
    <!-- Office Context Alert -->
    <v-alert
      v-if="officeId"
      type="info"
      density="compact"
      class="mb-4"
      closable
    >
      <v-row align="center" no-gutters>
        <v-col>
          <strong>Standort:</strong> {{ officeName || `ID: ${officeId}` }}
        </v-col>
        <v-col v-if="usingDefaults" class="text-right">
          <v-chip color="warning" size="small">
            <v-icon start>mdi-information</v-icon>
            Verwende Standardwerte
          </v-chip>
        </v-col>
        <v-col v-else class="text-right">
          <v-chip color="success" size="small">
            <v-icon start>mdi-check-circle</v-icon>
            Standort-spezifisch
          </v-chip>
        </v-col>
      </v-row>
    </v-alert>

    <!-- Loading Indicator -->
    <v-progress-circular
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-circular>

    <v-container
      fluid
      :style="`margin-top:10px; margin-bottom:30px; text-align:center; max-width: none;`"
    >
      <div>
        <h2>Parameter Übersicht</h2>
        <p>(Standardwerte in Klammern)</p>
      </div>
      <br />
      <!-- Zeitaufschläge -->
      <div><h3>Zeitaufschläge</h3></div>
      <br />
      <div style="overflow-x: auto; width: 100%">
        <table class="elevation-1" style="min-width: 100%; width: max-content">
          <thead>
            <tr>
              <th
                v-for="column in headersZeitaufschläge"
                :key="column.value"
                :style="{ width: column.width, minWidth: column.width }"
                style="font-weight: bold"
                class="text-center"
              >
                {{ column.text }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowKey) in editableParameters" :key="rowKey">
              <td
                v-for="column in headersZeitaufschläge"
                :key="column.value"
                style="padding: 5px"
              >
                <input
                  v-if="column.value === 'transport_type'"
                  v-model="transportTypeMap[row[column.value]]"
                />
                <input
                  v-else
                  :value="formatNumber(row[column.value])"
                  @change="(value) => handleInput(value, row, column)"
                />
                <span
                  v-if="
                    column.value !== 'transport_type' &&
                    defaultParameters &&
                    defaultParameters.length > 0
                  "
                  >({{
                    formatNumber(
                      getDefaultParameterByTransportType(row, column.value),
                    )
                  }})</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <br />
      <div><h3>CO₂ Ausstoß Parameter</h3></div>
      <br />
      <div style="overflow-x: auto; width: 100%">
        <table class="elevation-1" style="min-width: 100%; width: max-content">
          <thead>
            <tr>
              <th
                v-for="column in headersCO2Ausstoß"
                :key="column.value"
                :style="{ width: column.width, minWidth: column.width }"
                style="font-weight: bold"
                class="text-center"
              >
                {{ column.text }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowKey) in editableParameters" :key="rowKey">
              <td v-for="column in headersCO2Ausstoß" :key="column.value">
                <input
                  v-if="column.value === 'transport_type'"
                  v-model="transportTypeMap[row[column.value]]"
                />
                <input
                  v-else
                  :value="formatNumber(row[column.value])"
                  @change="(value) => handleInput(value, row, column)"
                />
                <span
                  v-if="
                    column.value !== 'transport_type' &&
                    defaultParameters &&
                    defaultParameters.length > 0
                  "
                  >({{
                    formatNumber(
                      getDefaultParameterByTransportType(row, column.value),
                    )
                  }})</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <br />
      <div><h3>Kosten Parameter</h3></div>
      <br />
      <div style="overflow-x: auto; width: 100%">
        <table class="elevation-1" style="min-width: 100%; width: max-content">
          <thead>
            <tr>
              <th
                v-for="column in headersKostenAnnahmen"
                :key="column.value"
                :style="{ width: column.width, minWidth: column.width }"
                style="font-weight: bold"
                class="text-center"
              >
                {{ column.text }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowKey) in editableParameters" :key="rowKey">
              <td v-for="column in headersKostenAnnahmen" :key="column.value">
                <input
                  v-if="column.value === 'transport_type'"
                  v-model="transportTypeMap[row[column.value]]"
                />
                <input
                  v-else
                  :value="formatNumber(row[column.value])"
                  @change="(value) => handleInput(value, row, column)"
                />
                <span
                  v-if="
                    column.value !== 'transport_type' &&
                    defaultParameters &&
                    defaultParameters.length > 0
                  "
                  >({{
                    formatNumber(
                      getDefaultParameterByTransportType(row, column.value),
                    )
                  }})</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <br />
      <div><h3>Parameter für Bewegung und Entfernungen</h3></div>
      <br />
      <div style="overflow-x: auto; width: 100%">
        <table class="elevation-1" style="min-width: 100%; width: max-content">
          <thead>
            <tr>
              <th
                v-for="column in headersBewegungUndEntfernungen"
                :key="column.value"
                :style="{ width: column.width, minWidth: column.width }"
                style="font-weight: bold"
                class="text-center"
              >
                {{ column.text }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowKey) in editableParameters" :key="rowKey">
              <td
                v-for="column in headersBewegungUndEntfernungen"
                :key="column.value"
              >
                <input
                  v-if="column.value === 'transport_type'"
                  v-model="transportTypeMap[row[column.value]]"
                />
                <input
                  v-else
                  :value="formatNumber(row[column.value])"
                  @change="(value) => handleInput(value, row, column)"
                />
                <span
                  v-if="
                    column.value !== 'transport_type' &&
                    defaultParameters &&
                    defaultParameters.length > 0
                  "
                  >({{
                    formatNumber(
                      getDefaultParameterByTransportType(row, column.value),
                    )
                  }})</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <br />

      <!-- Action Buttons -->
      <v-row class="mt-4" justify="center">
        <v-col cols="auto">
          <v-btn
            color="primary"
            @click="saveParameters"
            :disabled="loading || !officeId"
            :loading="loading"
            rounded="xl"
            size="large"
            elevation="10"
            variant="tonal"
          >
            <v-icon class="mr-2">mdi-content-save</v-icon>
            Parameter speichern
          </v-btn>
        </v-col>

        <v-col v-if="usingDefaults" cols="auto">
          <v-btn
            color="secondary"
            @click="initializeFromDefaults"
            :disabled="loading"
            rounded="xl"
            size="large"
            elevation="10"
            variant="tonal"
          >
            <v-icon class="mr-2">mdi-refresh</v-icon>
            Von Standardwerten initialisieren
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <v-btn
            color="error"
            @click="handleCancel"
            :disabled="loading"
            rounded="xl"
            size="large"
            elevation="10"
            variant="tonal"
          >
            <v-icon class="mr-2">mdi-close</v-icon>
            Abbrechen
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
      location="center center"
      elevation="6"
      rounded="pill"
    >
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" color="black" @click="showSnackbar = false">
          Schließen
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useCleverRouteImportStore } from "../store/cleverRouteImportStore";
import { useOfficeStore } from "../store/officeStore";
import { mapStores } from "pinia";

interface Column {
  text: string;
  width: string;
}

interface ColumnMapping {
  [key: string]: Column;
}

export default defineComponent({
  props: {
    selectedTransports: {
      type: Array,
      required: true,
    },
    // NEW: Office ID for office-specific parameters (replaces global parameter access)
    officeId: {
      type: Number,
      required: true,
      validator: (value: number) => value > 0,
    },
  },
  data() {
    return {
      headersZeitaufschläge: [
        {
          text: "Verkehrsmittel",
          value: "transport_type",
          width: "80px",
        },
        {
          text: "vor Abfahrt (min)",
          value: "added_time_start",
          width: "80px",
        },
        {
          text: "Parken (min)",
          value: "added_time_parking",
          width: "80px",
        },
        {
          text: "Fixer Aufschlag (min)",
          value: "added_time_fixed",
          width: "80px",
        },
        {
          text: "Relativer Aufschl. (%)",
          value: "added_time_relative",
          width: "80px",
        },
      ],
      headersCO2Ausstoß: [
        {
          text: "Verkehrsmittel",
          value: "transport_type",
          width: "80px",
        },
        { text: "CO₂ (g/km)", value: "co2_per_km", width: "80px" },
        { text: "CO₂ Fix (g)", value: "co2_fixed", width: "80px" },
      ],
      headersKostenAnnahmen: [
        {
          text: "Verkehrsmittel",
          value: "transport_type",
          width: "80px",
        },
        {
          text: "Kosten Fix (€/Jahr)",
          value: "cost_fixed_year",
          width: "80px",
        },
        {
          text: "Kosten Fix (€/Monat)",
          value: "cost_fixed_month",
          width: "80px",
        },
        {
          text: "Kosten Fix (€/Tag)",
          value: "cost_fixed_day",
          width: "80px",
        },
        { text: "€/km", value: "cost_per_km", width: "80px" },
      ],
      headersBewegungUndEntfernungen: [
        {
          text: "Verkehrsmittel",
          value: "transport_type",
          width: "80px",
        },
        {
          text: "Schritte/km",
          value: "sport_per_km",
          width: "80px",
        },
        {
          text: "Min Entfernung (km)",
          value: "range_min_km",
          width: "80px",
        },
        {
          text: "Max Entfernung (km)",
          value: "range_max_km",
          width: "80px",
        },
      ],
      editableParameters: [] as object[],
      changedRows: [] as object[],
      parametersReady: false,
      new_params: [] as object[],
      params: [] as object[],
      showParameters: false,

      // New office-specific state
      loading: false,
      initialized: false,
      showSnackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      tableColumns: [
        // "id",
        "transport_type",
        "speed",
        "added_time_start",
        "added_time_parking",
        "added_time_fixed",
        "added_time_relative",
        "added_time_sum",
        "co2_per_km",
        "co2_fixed",
        "cost_fixed_year",
        "cost_fixed_month",
        "cost_fixed_day",
        "cost_per_km",
        "sport_per_km",
        "range_min_km",
        "range_max_km",
      ],
      transportTypeMap: {
        car: "Pkw",
        car_electric: "E-Auto",
        train: "ÖV",
        park_and_ride: "Park & Ride",
        bike_and_ride: "Bike & Ride",
        eBike: "Pedelec",
        bike: "Fahrrad",
        walk: "Zu Fuß",
        // bikescooter: "BikeScooter",
        taxi: "Taxi",
        ride_and_taxi: "Taxi & Ride",
        // eScooter: "E-Scooter",
        // escooter_and_ride: "E-Scooter & Ride",
        // ferry: "Fähre",
        // carsharing: "Carsharing",
        werksbus: "Werksbus",
        bike_and_werksbus: "Bike & Werksbus",
        park_ride_and_bike: "Park & Ride & Bike",
      },
    };
  },
  emits: {
    "hide-form": () => {
      return true;
    },
    "delete-data": () => {
      return true;
    },
    "send-data": () => {
      return true;
    },
    "cancel-action": () => {
      return true;
    },
    "parameters-saved": () => {
      return true;
    },
  },
  computed: {
    ...mapStores(useCleverRouteImportStore, useOfficeStore),

    // NEW: Office-specific parameters (replaces global parameters)
    parameters() {
      return this.cleverRouteImportStore.getOfficeParameters;
    },
    defaultParameters() {
      // @ts-ignore
      return this.cleverRouteImportStore.defaultParameters;
    },
    filteredParameters() {
      return this.parameters.filter((row) =>
        this.selectedTransports.includes(
          row.transport_type || row.transportType,
        ),
      );
    },
    parametersUpdated() {
      // @ts-ignore
      return this.cleverRouteImportStore.parametersUpdated;
    },

    // NEW: Office parameter status getters
    usingDefaults() {
      return !this.cleverRouteImportStore.hasCustomParameters;
    },
    isParametersLoading() {
      return this.cleverRouteImportStore.isParametersLoading;
    },
    areParametersInitialized() {
      return this.cleverRouteImportStore.areParametersInitialized;
    },
    currentOfficeId() {
      return this.cleverRouteImportStore.getCurrentOfficeId;
    },

    // Get office name by ID for display
    officeName() {
      if (!this.officeId || !this.officeStore.getOffices) {
        return null;
      }
      const office = this.officeStore.getOffices.find(
        (o: any) => o.office_id === this.officeId,
      );
      return office ? office.office_name : null;
    },
  },
  async created() {
    // Keep default parameters for fallback display
    await this.cleverRouteImportStore.fetchDefaultParameters();
  },
  async mounted() {
    // Load office-specific parameters when component mounts
    // The watcher will handle office ID changes
    if (this.officeId) {
      await this.loadParameters();
    }
  },
  beforeUnmount() {
    // Clean up store state when component is destroyed
    this.cleverRouteImportStore.resetOfficeParameters();
  },
  methods: {
    hide() {
      this.$emit("hide-form");
    },
    getParameterValue(rowKey, columnValue) {
      // @ts-ignore
      const param = this.defaultParameters[rowKey]?.[columnValue];
      return param !== null && param !== undefined ? param : "0";
    },

    // NEW: Get default parameter value by matching transport type
    getDefaultParameterByTransportType(currentRow, columnValue) {
      if (!this.defaultParameters || !currentRow) {
        return "0";
      }

      // Get the transport type of the current row
      const currentTransportType =
        currentRow.transport_type || currentRow.transportType;

      // Find matching default parameter by transport type
      const matchingDefault = this.defaultParameters.find(
        (defaultParam) =>
          defaultParam.transport_type === currentTransportType ||
          defaultParam.transportType === currentTransportType,
      );

      if (
        matchingDefault &&
        matchingDefault[columnValue] !== null &&
        matchingDefault[columnValue] !== undefined
      ) {
        return matchingDefault[columnValue];
      }

      return "0";
    },
    onRowChanged(id: number) {
      if (!this.changedRows.includes(id)) {
        this.changedRows.push(id);
      }
      this.parametersReady = true;
    },
    /**
     * Load parameters for the current office (NEW: office-specific)
     */
    async loadParameters() {
      if (!this.officeId) {
        console.warn("Cannot load parameters: no office ID");
        this.showMessage("Kein Standort ausgewählt", "error");
        return;
      }

      this.loading = true;
      try {
        // Fetch parameters from store using office-specific method
        await this.cleverRouteImportStore.fetchOfficeParameters(this.officeId);

        this.editableParameters = JSON.parse(
          JSON.stringify(this.filteredParameters),
        );
        this.initialized = this.cleverRouteImportStore.areParametersInitialized;

        // Show info message if using defaults
        if (!this.initialized) {
          this.showMessage(
            "Verwende Standardwerte. Sie können diese initialisieren oder direkt ändern.",
            "info",
          );
        }
      } catch (error) {
        console.error("Error loading parameters:", error);
        this.showMessage(
          "Fehler beim Laden der Parameter: " + error.message,
          "error",
        );
      } finally {
        this.loading = false;
      }
    },

    /**
     * Save parameters for the current office (NEW: office-specific method replaces old saveParameters)
     */
    async saveParameters() {
      if (!this.officeId) {
        this.showMessage("Kein Standort ausgewählt", "error");
        return;
      }

      // Validate parameters before saving
      if (!this.validateParameters()) {
        this.showMessage("Bitte füllen Sie alle Pflichtfelder aus", "error");
        return;
      }

      this.loading = true;
      try {
        // Prepare parameters for API (ensure correct format)
        const paramsToSave = this.editableParameters.map((p) => {
          const updatedRow = this.updateData(p);
          return {
            id: updatedRow.id || p.id || null, // Preserve the ID for PUT operations
            // Only include field names that match database columns exactly
            transport_type: updatedRow.transport_type,
            speed: parseFloat(updatedRow.speed) || null,
            added_time_start: parseInt(updatedRow.added_time_start) || null,
            added_time_parking: parseInt(updatedRow.added_time_parking) || null,
            added_time_fixed: parseInt(updatedRow.added_time_fixed) || null,
            added_time_relative:
              parseInt(updatedRow.added_time_relative) || null,
            added_time_sum: parseInt(updatedRow.added_time_sum) || null,
            co2_per_km: parseFloat(updatedRow.co2_per_km) || null,
            co2_fixed: parseFloat(updatedRow.co2_fixed) || null,
            cost_fixed_year: parseFloat(updatedRow.cost_fixed_year) || null,
            cost_fixed_month: parseFloat(updatedRow.cost_fixed_month) || null,
            cost_fixed_day: parseFloat(updatedRow.cost_fixed_day) || null,
            cost_per_km: parseFloat(updatedRow.cost_per_km) || null,
            sport_per_km: parseFloat(updatedRow.sport_per_km) || null,
            range_min_km: parseFloat(updatedRow.range_min_km) || null,
            range_max_km: parseFloat(updatedRow.range_max_km) || null,
            office_id: this.officeId, // Database field name
          };
        });

        await this.cleverRouteImportStore.saveOfficeParameters(
          this.officeId,
          paramsToSave,
        );

        this.initialized = true;
        this.changedRows = []; // Clear changed rows after successful save
        this.showMessage("Parameter erfolgreich gespeichert!", "success");

        // Emit success event to parent
        this.$emit("parameters-saved");
      } catch (error) {
        console.error("Error saving parameters:", error);
        this.showMessage("Fehler beim Speichern: " + error.message, "error");
      } finally {
        this.loading = false;
      }
    },

    /**
     * Initialize parameters from defaults (NEW method)
     */
    async initializeFromDefaults() {
      if (!this.officeId) {
        this.showMessage("Kein Standort ausgewählt", "error");
        return;
      }

      if (
        !confirm(
          "Parameter von Standardwerten initialisieren? Ungespeicherte Änderungen gehen verloren.",
        )
      ) {
        return;
      }

      this.loading = true;
      try {
        await this.cleverRouteImportStore.initializeOfficeParameters(
          this.officeId,
        );

        // Reload parameters after initialization
        await this.loadParameters();

        this.showMessage(
          "Parameter von Standardwerten initialisiert",
          "success",
        );
      } catch (error) {
        console.error("Error initializing parameters:", error);
        this.showMessage(
          "Fehler beim Initialisieren: " + error.message,
          "error",
        );
      } finally {
        this.loading = false;
      }
    },

    /**
     * Validate parameters before saving (NEW method)
     */
    validateParameters() {
      // Check that all parameters have required fields
      for (const param of this.editableParameters) {
        if (!param.transport_type) {
          return false;
        }
        // Add more validation as needed
      }
      return true;
    },

    /**
     * Show snackbar message (NEW method)
     */
    showMessage(message: string, color: string = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    },

    /**
     * Handle cancel action (NEW method)
     */
    handleCancel() {
      // Reset any unsaved changes
      this.cleverRouteImportStore.resetOfficeParameters();
      this.$emit("cancel-action");
      this.$emit("hide-form");
    },
    updateData(row) {
      const integerColumns = [
        "added_time_start",
        "added_time_parking",
        "added_time_fixed",
        "added_time_relative",
        "added_time_sum",
      ];
      const floatColumns = [
        "speed",
        "co2_per_km",
        "co2_fixed",
        "cost_fixed_year",
        "cost_fixed_month",
        "cost_fixed_day",
        "cost_per_km",
        "sport_per_km",
        "range_min_km",
        "range_max_km",
      ];

      // Convert each property in 'row' to an integer, except for 'transport_type'
      const updatedRow = Object.fromEntries(
        Object.entries(row).map(([key, value]) => {
          if (Number.isNaN(value)) {
            value = 0;
          } else if (integerColumns.includes(key)) {
            value = parseInt(value);
          } else if (floatColumns.includes(key)) {
            value = parseFloat(value);
          }
          return [key, value];
        }),
      );
      return updatedRow;
    },
    async setDefaultParameters() {
      for (const key in this.filteredParameters) {
        if (
          Object.prototype.hasOwnProperty.call(this.filteredParameters, key)
        ) {
          this.editableParameters[key] = JSON.parse(
            JSON.stringify(this.defaultParameters[key]),
          );
        }
      }
      this.parametersReady = true;
    },
    formatNumber(value) {
      return Number(value).toLocaleString("de-DE");
    },
    // @ts-ignore
    handleInput(event, row, column) {
      const value = event.target.value;
      if (value === "") {
        row[column.value] = null;
      } else {
        row[column.value] = parseFloat(value);
      }
      this.onRowChanged(row.id);
    },
  },

  // NEW: Watchers for office-specific parameter management
  watch: {
    // Reload parameters when office changes
    officeId: {
      immediate: false, // Don't run immediately since mounted() handles initial load
      async handler(newOfficeId, oldOfficeId) {
        if (newOfficeId && newOfficeId !== oldOfficeId) {
          await this.loadParameters();
        }
      },
    },

    // Reload when selected transports change
    selectedTransports: {
      deep: true,
      async handler(newTransports, oldTransports) {
        if (
          this.initialized &&
          JSON.stringify(newTransports) !== JSON.stringify(oldTransports)
        ) {
          // Update filtered parameters when transport selection changes
          this.editableParameters = JSON.parse(
            JSON.stringify(this.filteredParameters),
          );
        }
      },
    },
  },
});
</script>

<style scoped>
.button-container {
  display: flex;
  justify-content: s;
}

/* Add background styling for better readability over the hero image */
.v-container {
  backdrop-filter: blur(4px);
  background-color: rgba(255, 255, 255, 0.9) !important;
  border-radius: 8px;
}

/* Enhanced table styling for better readability */
table {
  backdrop-filter: blur(4px);
  background-color: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px;
  overflow: hidden;
}

table th {
  background-color: rgba(255, 255, 255, 0.9) !important;
}

table tr:nth-child(even) {
  background-color: rgba(240, 240, 240, 0.9) !important;
}

table tr:nth-child(odd) {
  background-color: rgba(255, 255, 255, 0.8) !important;
}

/* Button styling for consistency */
.v-btn {
  backdrop-filter: blur(2px);
  background-color: rgba(255, 255, 255, 0.85) !important;
}
</style>
